import React, { useState } from 'react';
import { Eye, Shield, Sparkles, X, Loader2 } from 'lucide-react';
import { api } from '../context/AppContext';
import { toast } from 'sonner';

/**
 * Posture Vision Fallback Consent Dialog (Iter. 405.c)
 *
 * Shown ONLY when the client-side BlazePose scan failed to land enough
 * landmarks (severity === "unavailable" OR no metrics extracted). Gives
 * the user an explicit cloud-upload consent toggle before sending the
 * just-uploaded photo to Gemini Vision for analysis.
 *
 * Props
 *   photoId    string  — the photo_id returned by POST /evolution/photos
 *   isOpen     bool    — controlled visibility
 *   onClose    fn      — close without running fallback
 *   onSuccess  fn(posture_analysis) — fired with the Vision result so the
 *                                     parent can refresh its photo list
 */
export default function PostureVisionFallbackDialog({ photoId, isOpen, onClose, onSuccess }) {
  const [consent, setConsent] = useState(false);
  const [running, setRunning] = useState(false);

  if (!isOpen) return null;

  const handleRun = async () => {
    if (!consent || !photoId) return;
    setRunning(true);
    try {
      const res = await api.post(`/evolution/photos/${photoId}/vision-fallback`);
      const analysis = res.data?.posture_analysis;
      toast.success('Analyse Gemini Vision terminée');
      if (onSuccess) onSuccess(analysis);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Analyse Vision indisponible';
      toast.error(detail);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      data-testid="posture-vision-fallback-dialog"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={() => !running && onClose()}
    >
      <div
        className="bg-zinc-900 text-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold leading-tight">Posture non détectée</h2>
              <p className="text-xs text-white/60 leading-tight mt-0.5">
                BlazePose (local) n'a pas pu lire ta posture sur cette photo.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !running && onClose()}
            className="p-1 rounded-full hover:bg-white/10 transition disabled:opacity-30"
            disabled={running}
            aria-label="Fermer"
            data-testid="posture-vision-fallback-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <p className="text-white/80 leading-relaxed">
            Veux-tu activer l'<span className="font-semibold">analyse Gemini Vision</span> en
            secours&nbsp;? Ta photo sera envoyée à notre IA pour extraire les mêmes
            asymétries qu'une analyse locale.
          </p>

          <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2">
            <div className="flex items-start gap-2 text-xs text-white/70">
              <Eye className="w-3.5 h-3.5 mt-0.5 shrink-0 text-violet-300" />
              <span>
                <span className="font-semibold text-white">Plan B.</span> BlazePose
                reste la méthode par défaut (rapide, local, privée).
              </span>
            </div>
            <div className="flex items-start gap-2 text-xs text-white/70">
              <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-300" />
              <span>
                Ta photo est traitée temporairement et n'est pas stockée par Gemini.
                Le résultat clinique reste dans ta Voûte Évolution.
              </span>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 rounded border-white/30 bg-transparent accent-violet-500"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="posture-vision-fallback-consent"
            />
            <span className="text-xs text-white/75 leading-snug">
              J'accepte l'envoi sécurisé de cette photo à l'IA de vision pour
              extraire mes métriques posturales.
            </span>
          </label>
        </div>

        <div className="flex gap-2 p-4 border-t border-white/10">
          <button
            type="button"
            onClick={() => !running && onClose()}
            className="flex-1 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-sm font-medium transition disabled:opacity-50"
            disabled={running}
            data-testid="posture-vision-fallback-cancel"
          >
            Plus tard
          </button>
          <button
            type="button"
            onClick={handleRun}
            disabled={!consent || running}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-sm font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="posture-vision-fallback-run"
          >
            {running ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyse…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Lancer Vision
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
